import { useState, useMemo, useEffect, useRef } from "react";
import styles from "./PaymentFilterGroup.module.scss";
import MenuDropdown from "@Components/MenuDropdown";
import RadioButton from "@Components/Radio";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import PaymentChevronDown from "@Icons/PaymentChevronDown";
import useIsDesktop from "hooks/useIsDesktop";
import useTranslationHook from "hooks/useTranslationHook";
import Portal from "@Components/Portal";
import BottomSheetOverlay from "@Components/BottomSheetOverlay";
import wrapperHOC from "@Utils/wrapperHOC";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";

interface PaymentFilterGroupProps {
  filtersData: {
    duration: string[];
    year: string[];
  };
  setDuration: (duration: string) => void;
  setYear: (year: string) => void;
  isYearOpen?: boolean;
  setIsYearOpen?: (open: boolean) => void;
  setIsDurationOpen?: (open: boolean) => void;
  forceCloseYear?: boolean;
  setForceCloseDuration?: (close: boolean) => void;
}

const PaymentFilterGroup = ({
  filtersData,
  setDuration,
  setYear,
  isYearOpen: externalIsYearOpen,
  setIsYearOpen: externalSetIsYearOpen,
  setIsDurationOpen,
  forceCloseYear = false,
  setForceCloseDuration,
}: PaymentFilterGroupProps) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  const [activeFilters, setActiveFilters] = useState({ year: false });
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [tempSelectedYear, setTempSelectedYear] = useState<string>("");
  const [showYearModal, setShowYearModal] = useState(false);
  const [yearClose, setYearClose] = useState(false);
  const hasAutoSelectedYear = useRef(false);

  // Use external state if provided, otherwise use internal state
  const [internalIsYearOpen, setInternalIsYearOpen] = useState(false);
  const isYearOpen = externalIsYearOpen !== undefined ? externalIsYearOpen : internalIsYearOpen;
  const setIsYearOpen = externalSetIsYearOpen || setInternalIsYearOpen;

  // Handle force close from parent
  useEffect(() => {
    if (forceCloseYear) {
      setActiveFilters((prev) => ({ ...prev, year: false }));
      setIsYearOpen(false);
      setYearClose(true);
      setTimeout(() => setYearClose(false), 100);
    }
  }, [forceCloseYear]);

  // Set duration to "All" by default
  useEffect(() => {
    setDuration("All");
  }, [setDuration]);

  const availableYears = useMemo(() => filtersData.year, [filtersData.year]);

  const handleYearSelect = (year: string) => {
    if (isDesktop) {
      setSelectedYear(year);
      setYear(year);
      setIsYearOpen(false);
      setActiveFilters((prev) => ({ ...prev, year: false }));
      setYearClose(true);
      setTimeout(() => setYearClose(false), 100);
      gtagEventEmitter({
        action: "Payment_year_selected",
        category: PAGE.INVENTORY,
        label: "success",
      });
    } else {
      setTempSelectedYear(year);
      gtagEventEmitter({
        action: "Payment_year_selected",
        category: PAGE.INVENTORY,
        label: "success",
      });
    }
  };

  const resetClickYear = () => {
    // When single year is preselected, just close the dropdown without resetting
    if (availableYears.length === 1) {
      if (isDesktop) {
        setIsYearOpen(false);
        setYearClose(true);
        setActiveFilters((prev) => ({ ...prev, year: false }));
        setTimeout(() => setYearClose(false), 100);
      } else {
        setShowYearModal(false);
      }
      return;
    }

    if (isDesktop) {
      setSelectedYear("");
      setYear("All");
      setIsYearOpen(false);
      setYearClose(true);
      setActiveFilters((prev) => ({ ...prev, year: false }));
      setTimeout(() => setYearClose(false), 100);
      gtagEventEmitter({
        action: "Payment_year_reset",
        category: PAGE.INVENTORY,
        label: "success",
      });
    } else {
      setTempSelectedYear("");
      setSelectedYear("");
      setYear("All");
      setShowYearModal(false);
      gtagEventEmitter({
        action: "Payment_year_reset",
        category: PAGE.INVENTORY,
        label: "success",
      });
    }
  };

  const applyYearFilter = () => {
    setSelectedYear(tempSelectedYear);
    setYear(tempSelectedYear || "All");
    setShowYearModal(false);
    gtagEventEmitter({
      action: "Payment_year_applied",
      category: PAGE.INVENTORY,
      label: "success",
    });
  };

  const yearMenu = (
    <div className={styles.dropdownMenu} role="menu">
      <div className={styles.heading_filter}>
        <div className={styles.heading}>{t("Year")}</div>
        <div
          className={formatClassNames(styles.reset, !selectedYear && styles.inactive)}
          onClick={() => {
            if (selectedYear) resetClickYear();
          }}
          role="none"
        >
          {t("Reset")}
        </div>
      </div>
      <div className={styles.divider} />
      <div className={styles.listItems}>
        {availableYears.map((year) => (
          <div
            className={styles.item}
            key={`year-${year}`}
            onClick={() => handleYearSelect(year)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleYearSelect(year);
              }
            }}
            role="none"
          >
            <RadioButton isSelected={year === selectedYear} />
            <span className={styles.filterDropdownText}>
              {year.replace("ay_", "").replace("_", "-")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const onClickYear = () => {
    if (isDesktop) {
      const willOpen = !isYearOpen;
      setIsYearOpen(willOpen);
      setActiveFilters((prev) => ({ ...prev, year: willOpen }));

      // Close duration filter when year opens
      if (willOpen) {
        if (setIsDurationOpen) {
          setIsDurationOpen(false);
        }
        if (setForceCloseDuration) {
          setForceCloseDuration(true);
          setTimeout(() => setForceCloseDuration(false), 100);
        }
      }

      gtagEventEmitter({
        action: "Payment_year_clicked",
        category: PAGE.INVENTORY,
        label: "success",
      });
    } else {
      setTempSelectedYear(selectedYear);
      setShowYearModal(true);
      gtagEventEmitter({
        action: "Payment_year_clicked",
        category: PAGE.INVENTORY,
        label: "success",
      });
    }
  };

  useEffect(() => {
    if (availableYears.length === 1 && !hasAutoSelectedYear.current) {
      hasAutoSelectedYear.current = true;
      if (isDesktop) {
        setSelectedYear(availableYears[0]);
        setYear(availableYears[0]);
        setIsYearOpen(false);
      } else {
        setTempSelectedYear(availableYears[0]);
        setSelectedYear(availableYears[0]);
        setYear(availableYears[0]);
        setShowYearModal(false);
      }
    }
  }, [availableYears]);

  return (
    <>
      <div className={styles.container}>
        <div>
          <MenuDropdown
            target={
              <div
                className={formatClassNames(
                  styles.badge,
                  selectedYear && styles.selected,
                  activeFilters?.year && styles.active,
                )}
                onClick={onClickYear}
                role="none"
              >
                {selectedYear ? selectedYear.replace("ay_", "").replace("_", "-") : t("Year")}
                <div
                  className={formatClassNames(
                    styles.icon,
                    selectedYear && styles.selected,
                    activeFilters?.year && styles.active,
                  )}
                >
                  <PaymentChevronDown
                    width={isDesktop ? 16 : 12}
                    height={isDesktop ? 16 : 12}
                    className={isYearOpen ? styles.open : ""}
                  />
                </div>
              </div>
            }
            menu={isDesktop ? yearMenu : null}
            handleOnClose={() => {
              setActiveFilters((prev) => ({ ...prev, year: false }));
              setIsYearOpen(false);
            }}
            forceClose={yearClose}
          />
        </div>
      </div>

      {!isDesktop && showYearModal && (
        <Portal rootId="forPortal">
          <BottomSheetOverlay
            hideBottomSheetOverlay={() => setShowYearModal(false)}
            isHistoryPush={!isDesktop}
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.filterMobileHeading}>{t("Year")}</h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowYearModal(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {availableYears.map((year) => (
                <div
                  className={styles.modalItem}
                  key={`year-modal-${year}`}
                  onClick={() => handleYearSelect(year)}
                  role="none"
                >
                  <RadioButton isSelected={year === tempSelectedYear} />
                  <span className={styles.filterMobileModalText}>
                    {year.replace("ay_", "").replace("_", "-")}
                  </span>
                </div>
              ))}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={formatClassNames(
                  styles.clearButton,
                  !tempSelectedYear && styles.inactive,
                )}
                onClick={() => {
                  if (tempSelectedYear) resetClickYear();
                }}
                type="button"
              >
                {t("Clear")}
              </button>
              <button className={styles.applyButton} onClick={applyYearFilter} type="button">
                {t("Apply")}
              </button>
            </div>
          </BottomSheetOverlay>
        </Portal>
      )}
    </>
  );
};

export default wrapperHOC(PaymentFilterGroup, {
  componentName: "PaymentFilterGroup-PaymentAndFees-PropertyPage",
  showForChina: true,
});
